import {
  FONT_FACE_STYLE_TAG_ID,
  FONT_RESOURCE_STATE_ATTRIBUTE,
  GOOGLE_FONT_LINK_TAG_ID,
} from '@/lib/embed/styleFontResources';
import {
  extractPrimaryFontFamily,
  getRemoteFontFamilies,
  isSafeFontFamilyName,
} from '@/lib/embed/fontFamilies';
import {
  ALLOWED_STYLE_TOKENS,
  DEFAULT_ATRIVIAL_TOKENS,
  EMAIL_QUIZ_STYLE_TOKENS,
  mergeTokenLayers,
  pickEmailQuizStyleTokens,
} from '../../../../supabase/functions/_shared/emailStyleTokens.ts';
import type {
  AllowedStyleToken,
  EmailQuizStyleToken,
} from '../../../../supabase/functions/_shared/emailStyleTokens.ts';

export {
  ALLOWED_STYLE_TOKENS,
  DEFAULT_ATRIVIAL_TOKENS,
  EMAIL_QUIZ_STYLE_TOKENS,
  mergeTokenLayers,
  pickEmailQuizStyleTokens,
};
export type { AllowedStyleToken, EmailQuizStyleToken };

/**
 * App-specific style validation and browser font resource management.
 * Token definitions and merge behavior are shared with newsletter rendering.
 */
// =============================================================================
// PAYLOAD TYPE
// =============================================================================

export type StyleOverrideFontFormat = 'woff2' | 'woff' | 'truetype' | 'opentype';

export interface StyleOverrideFontFace {
  family: string;
  src: string;
  format: StyleOverrideFontFormat;
}

// =============================================================================
// TOKEN CATEGORIES (for UI grouping)
// =============================================================================

type TokenCategory = 'color' | 'radius' | 'image' | 'font' | 'dimension';

const TOKEN_CATEGORY_MAP: Record<AllowedStyleToken, TokenCategory> = {
  '--bg-primary': 'color',
  '--bg-raised': 'color',
  '--bg-subtle': 'color',
  '--bg-gradient': 'image',
  '--bg-noise': 'image',
  '--glyph-primary': 'color',
  '--glyph-secondary': 'color',
  '--accent-primary': 'color',
  '--outline-default': 'color',
  '--glyph-success': 'color',
  '--glyph-critical': 'color',
  '--radius-md': 'radius',
  '--radius-lg': 'radius',
  '--radius-full': 'radius',
  '--font-family-title': 'font',
  '--font-family-body': 'font',
  '--font-size-body': 'dimension',
};

export function getTokenCategory(token: AllowedStyleToken): TokenCategory {
  return TOKEN_CATEGORY_MAP[token];
}

// =============================================================================
// VALIDATION
// =============================================================================

const COLOR_REGEX = /^(#[0-9a-fA-F]{3,8}|rgba?\s*\(|hsla?\s*\(|oklch\s*\()/;
const DIMENSION_REGEX = /^\d+(\.\d+)?(px|rem|em|vh|vw|%)$/;
const RADIUS_REGEX = /^\d+(\.\d+)?(px|rem)$/;
const FONT_REGEX = /^['"a-zA-Z0-9\s,-]+$/;
const ALLOWED_FONT_FORMATS = new Set<StyleOverrideFontFormat>([
  'woff2',
  'woff',
  'truetype',
  'opentype',
]);

const MAX_FONT_FACES = 8;

function isValidTokenValue(token: AllowedStyleToken, value: string): boolean {
  // Allow strict "none" or "transparent" for specific tokens
  if (value === 'none' || value === 'transparent') return true;

  const category = TOKEN_CATEGORY_MAP[token];
  if (category === 'color') {
    return COLOR_REGEX.test(value);
  }
  if (category === 'radius') {
    return RADIUS_REGEX.test(value);
  }
  if (category === 'dimension') {
    return DIMENSION_REGEX.test(value);
  }
  if (category === 'font') {
    return FONT_REGEX.test(value);
  }
  if (category === 'image') {
    // Basic validation for gradients/urls, or just allow strings for now if strictly controlled by UI
    // For now, we only intend to set these to "none" via the UI, but let's allow safe CSS strings
    return (
      value === 'none' ||
      value.startsWith('url(') ||
      value.startsWith('linear-gradient(') ||
      value.startsWith('radial-gradient(')
    );
  }
  return false;
}

const allowedTokenSet = new Set<string>(ALLOWED_STYLE_TOKENS);

function sanitizeFontFaceSource(src: unknown): string | null {
  if (typeof src !== 'string') return null;

  const trimmed = src.trim();
  if (!trimmed || trimmed.length > 1024) return null;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:') return parsed.toString();
    if (
      parsed.protocol === 'http:' &&
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')
    ) {
      return parsed.toString();
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Sanitizes optional font-face payload entries.
 */
export function sanitizeStyleOverrideFontFaces(faces: unknown): StyleOverrideFontFace[] {
  if (!Array.isArray(faces)) return [];

  const result: StyleOverrideFontFace[] = [];
  const seen = new Set<string>();

  for (const face of faces) {
    if (!face || typeof face !== 'object') continue;

    const entry = face as Partial<StyleOverrideFontFace>;
    const family = typeof entry.family === 'string' ? entry.family.trim() : '';
    const src = sanitizeFontFaceSource(entry.src);
    const format = entry.format;

    if (!family || !isSafeFontFamilyName(family)) continue;
    if (!src) continue;
    if (!format || !ALLOWED_FONT_FORMATS.has(format)) continue;

    const dedupeKey = `${family.toLowerCase()}::${src}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    result.push({ family, src, format });
    if (result.length >= MAX_FONT_FACES) break;
  }

  return result;
}

/**
 * Filters a token map to only include allowed keys with valid values.
 * Unknown keys and invalid values are silently dropped.
 */
export function sanitizeStyleOverrideTokens(
  tokens: Record<string, string>
): Partial<Record<AllowedStyleToken, string>> {
  const normalized: Record<string, string> = { ...tokens };
  if (normalized['--color-error'] && !normalized['--glyph-critical']) {
    normalized['--glyph-critical'] = normalized['--color-error'];
  }
  delete normalized['--color-error'];

  const result: Partial<Record<AllowedStyleToken, string>> = {};

  for (const [key, value] of Object.entries(normalized)) {
    if (!allowedTokenSet.has(key)) continue;
    const token = key as AllowedStyleToken;
    if (typeof value !== 'string' || !value.trim()) continue;
    if (!isValidTokenValue(token, value.trim())) continue;
    result[token] = value.trim();
  }

  return result;
}

// =============================================================================
// DOM APPLICATION
// =============================================================================

function removeElementById(id: string): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (el) el.remove();
}

function buildGoogleFontHref(families: string[]): string {
  const query = families
    .map((family) => `family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;500;700`)
    .join('&');

  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}

function escapeCssString(input: string): string {
  return input.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Applies additional font resources required by style overrides:
 * - Google Fonts (for curated Google families)
 * - Custom uploaded @font-face definitions passed in payload
 */
export function applyStyleOverrideFonts(
  tokens: Partial<Record<AllowedStyleToken, string>>,
  fontFaces: StyleOverrideFontFace[] = []
): void {
  if (typeof document === 'undefined') return;

  const safeFontFaces = sanitizeStyleOverrideFontFaces(fontFaces);
  const safeUploadedFamilies = safeFontFaces.map((face) => face.family);
  const googleFamilies = getRemoteFontFamilies(
    {
      '--font-family-title': tokens['--font-family-title'],
      '--font-family-body': tokens['--font-family-body'],
    },
    safeUploadedFamilies
  );
  const googleHref = googleFamilies.length > 0 ? buildGoogleFontHref(googleFamilies) : null;
  const existingGoogleLink = document.getElementById(GOOGLE_FONT_LINK_TAG_ID);
  if (googleHref === null) {
    existingGoogleLink?.remove();
  } else if (existingGoogleLink?.getAttribute('href') !== googleHref) {
    existingGoogleLink?.remove();
    const link = document.createElement('link');
    link.id = GOOGLE_FONT_LINK_TAG_ID;
    link.rel = 'stylesheet';
    link.href = googleHref;
    link.setAttribute(FONT_RESOURCE_STATE_ATTRIBUTE, 'pending');
    link.addEventListener('load', () => {
      link.setAttribute(FONT_RESOURCE_STATE_ATTRIBUTE, 'ready');
    });
    link.addEventListener('error', () => {
      link.setAttribute(FONT_RESOURCE_STATE_ATTRIBUTE, 'failed');
    });
    document.head.appendChild(link);
  }

  const selectedFamilies = new Set<string>();
  const titleFamily = extractPrimaryFontFamily(tokens['--font-family-title'] ?? '');
  const bodyFamily = extractPrimaryFontFamily(tokens['--font-family-body'] ?? '');
  if (titleFamily) selectedFamilies.add(titleFamily.toLowerCase());
  if (bodyFamily) selectedFamilies.add(bodyFamily.toLowerCase());

  const safeFaces = safeFontFaces.filter((face) => selectedFamilies.has(face.family.toLowerCase()));
  const fontFaceCss = safeFaces
    .map(
      (face) =>
        `@font-face{font-family:'${escapeCssString(face.family)}';src:url('${escapeCssString(face.src)}') format('${face.format}');font-display:swap;font-style:normal;font-weight:400;}`
    )
    .join('\n');
  const existingFontFaceStyle = document.getElementById(FONT_FACE_STYLE_TAG_ID);
  if (!fontFaceCss) {
    existingFontFaceStyle?.remove();
  } else if (existingFontFaceStyle?.textContent !== fontFaceCss) {
    existingFontFaceStyle?.remove();
    const style = document.createElement('style');
    style.id = FONT_FACE_STYLE_TAG_ID;
    style.textContent = fontFaceCss;
    document.head.appendChild(style);
  }
}

/**
 * Removes any dynamically injected Google font stylesheet / @font-face tags.
 */
export function clearStyleOverrideFonts(): void {
  removeElementById(GOOGLE_FONT_LINK_TAG_ID);
  removeElementById(FONT_FACE_STYLE_TAG_ID);
}
